import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Chip, Tooltip } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTemperatureHigh } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { LocationViewModel } from '../../models/locations';
import SquareMetersPerPigChip from '../FarmLocations/SquareMetersPerPigChip';

interface PenCardProps {
  location: LocationViewModel;
  hasWelfareFeature: boolean;
  onClick?: () => void;
  isOverlay?: boolean;
}

/** Pure presentational pen card. Reuses the same visual cues as the flat locations table. */
export const PenCard: React.FC<PenCardProps> = ({ location, hasWelfareFeature, onClick, isOverlay }) => {
  const { t } = useTranslation();

  const isInactive = location.isSoftDeleted;
  const isAreaNonCompliant = hasWelfareFeature && location.isAreaCompliant === false;

  const borderClass = isAreaNonCompliant ? 'border-red-300' : 'border-gray-200';
  const inactiveClass = isInactive ? 'opacity-60 bg-gray-50' : 'bg-white';
  const overlayClass = isOverlay ? 'shadow-lg rotate-1' : 'shadow-sm hover:shadow-md';

  return (
    <div
      className={`rounded-lg border ${borderClass} ${inactiveClass} ${overlayClass} p-3 cursor-pointer transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-semibold text-gray-800 text-sm">
          {t('f-loc-pen')} {location.pen || '-'}
        </span>
        {location.number && <Chip size="small" variant="outlined" label={location.number} />}
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <span>
          {t('f-loc-hier-animals')}: <span className="font-medium text-gray-800">{location.animalsCount ?? 0}</span>
        </span>
        <span className={isAreaNonCompliant ? 'font-bold text-red-600' : ''}>
          {location.area ? t('f-loc-area-value', { value: location.area.areaInM || 0 }) : '-'}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 mt-2">
        <SquareMetersPerPigChip areaInM={location.area?.areaInM} animalsCount={location.animalsCount} />
        <div className="flex items-center gap-1">
          {/* Reads `hasSensorDevice` off the location payload. The tooltip used to be
              `metadata.climateDeviceNumber` - a number nobody could match to anything, and a field nothing
              writes any more - and is now the device's own name at the provider, which is the string a
              farmer can actually recognise. */}
          {location.hasSensorDevice && (
            <Tooltip arrow title={location.sensorDevice?.name || t('f-loc-climate')}>
              <span className="inline-flex">
                <FontAwesomeIcon icon={faTemperatureHigh} className="text-gray-500" />
              </span>
            </Tooltip>
          )}
          {isInactive && (
            <Chip size="small" label={t('f-loc-inactive')} className="!bg-gray-400 !text-white !text-[0.65rem]" />
          )}
        </div>
      </div>
    </div>
  );
};

interface SortablePenProps {
  location: LocationViewModel;
  hasWelfareFeature: boolean;
  onClick: () => void;
}

/** Draggable wrapper around PenCard for use inside a SortableContext. */
const SortablePen: React.FC<SortablePenProps> = ({ location, hasWelfareFeature, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: location.id
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PenCard location={location} hasWelfareFeature={hasWelfareFeature} onClick={onClick} />
    </div>
  );
};

export default SortablePen;
